import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function MortbuzzScrollHome() {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Simulate 3D house movement with DOM elements
      const house = document.querySelector('.house-3d');
      
      if (!house) return;

      // Create a timeline for house movement
      const houseTL = gsap.timeline({
        scrollTrigger: {
          trigger: ".content",
          start: "top top",
          end: "bottom bottom",
          scrub: true,
          pin: true
        }
      });

      // House flying animation sequence
      houseTL
        // Start position (off-screen right)
        .set(house, { 
          x: '100vw', 
          y: '50vh', 
          rotationY: -90,
          scale: 0.5 
        })
        
        // Fly in from right
        .to(house, { 
          x: '50vw', 
          y: '40vh', 
          rotationY: 0,
          scale: 0.8,
          duration: 2 
        })
        
        // Move up and rotate
        .to(house, { 
          y: '20vh', 
          rotationY: 45,
          scale: 1,
          duration: 1.5 
        })
        
        // Fly across screen
        .to(house, { 
          x: '20vw', 
          y: '30vh', 
          rotationY: 90,
          duration: 2 
        })
        
        // Circle around
        .to(house, { 
          x: '60vw', 
          y: '10vh', 
          rotationY: 180,
          rotationX: 20,
          duration: 2 
        })
        
        // Final position
        .to(house, { 
          x: '40vw', 
          y: '50vh', 
          rotationY: 360,
          rotationX: 0,
          scale: 1.2,
          duration: 2 
        });

      // Section text animations
      gsap.utils.toArray('.section').forEach((section, index) => {
        gsap.fromTo(section, 
          { opacity: 0, y: 100 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

    }, containerRef);

    return () => ctx.revert();
  }, []);

  const styles = {
    container: {
      fontFamily: "'Inter', sans-serif",
      background: 'linear-gradient(135deg, #0A2540 0%, #1a2980 100%)',
      color: 'white',
      overflowX: 'hidden',
      position: 'relative',
      minHeight: '100vh'
    },
    content: {
      position: 'relative',
      zIndex: 2
    },
    house3D: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      width: '200px',
      height: '200px',
      transformStyle: 'preserve-3d',
      transition: 'transform 0.1s',
      zIndex: 5
    },
    houseCube: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      transformStyle: 'preserve-3d',
      animation: 'rotateHouse 20s infinite linear'
    },
    houseFace: {
      position: 'absolute',
      width: '200px',
      height: '200px',
      background: 'linear-gradient(45deg, #8B4513, #A0522D)',
      border: '2px solid #654321',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      color: 'white',
      opacity: 0.8
    },
    houseRoof: {
      position: 'absolute',
      width: '0',
      height: '0',
      borderLeft: '100px solid transparent',
      borderRight: '100px solid transparent',
      borderBottom: '100px solid #B22222'
    },
    section: {
      position: 'relative',
      padding: '10vmin',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      zIndex: 3
    },
    h1: {
      fontFamily: "'Playfair Display', serif",
      fontSize: 'clamp(40px, 8vw, 64px)',
      fontWeight: '900',
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '1rem'
    },
    h2: {
      fontFamily: "'Playfair Display', serif",
      fontSize: 'clamp(32px, 6vw, 48px)',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #00D4AA 0%, #4ECDC4 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '1rem'
    },
    p: {
      fontSize: 'clamp(16px, 3vw, 20px)',
      opacity: '0.9',
      maxWidth: '600px',
      lineHeight: '1.6'
    }
  };

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;600;700&display=swap');
      
      @keyframes rotateHouse {
        0% { transform: rotateY(0deg) rotateX(10deg); }
        100% { transform: rotateY(360deg) rotateX(10deg); }
      }
      
      .house-window {
        width: 30px;
        height: 40px;
        background: linear-gradient(45deg, #87CEEB, #E0F7FF);
        border: 2px solid #1E90FF;
        border-radius: 5px;
        margin: 5px;
      }
      
      .house-door {
        width: 40px;
        height: 60px;
        background: linear-gradient(45deg, #8B4513, #A0522D);
        border: 2px solid #654321;
        position: absolute;
        bottom: 0;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div ref={containerRef} style={styles.container}>
      {/* 3D House Element */}
      <div className="house-3d" style={styles.house3D}>
        <div className="house-cube" style={styles.houseCube}>
          {/* House Base */}
          <div style={{
            ...styles.houseFace,
            transform: 'translateZ(100px)',
            background: 'linear-gradient(45deg, #8B4513, #A0522D)'
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div className="house-window"></div>
              <div className="house-window"></div>
            </div>
            <div className="house-door"></div>
          </div>
          
          {/* Roof */}
          <div style={{
            ...styles.houseRoof,
            transform: 'translateY(-100px) rotateX(90deg)',
            borderBottom: '100px solid #B22222'
          }}></div>
        </div>
      </div>

      <div className="content" style={styles.content}>
        {/* Section 1: Hero */}
        <div className="section" style={styles.section}>
          <h1 style={styles.h1}>Home Loans</h1>
          <p style={styles.p}>Watch your dream home come to life as you scroll...</p>
        </div>

        {/* Section 2: Problem */}
        <div className="section" style={styles.section}>
          <h2 style={styles.h2}>Finding the perfect loan</h2>
          <p style={styles.p}>Can feel overwhelming, but we're here to guide you</p>
        </div>

        {/* Section 3: Solution */}
        <div className="section" style={styles.section}>
          <h2 style={styles.h2}>We simplify the process</h2>
          <p style={styles.p}>Making your journey smooth and stress-free</p>
        </div>

        {/* Section 4: Features */}
        <div className="section" style={styles.section}>
          <h2 style={styles.h2}>Lowest rates guaranteed</h2>
          <p style={styles.p}>Save more while building your dream</p>
        </div>

        {/* Section 5: Final CTA */}
        <div className="section" style={styles.section}>
          <h2 style={styles.h2}>Ready to begin?</h2>
          <p style={styles.p}>Your dream home is waiting</p>
          <button style={{
            background: 'linear-gradient(135deg, #00D4AA, #4ECDC4)',
            border: 'none',
            padding: '15px 30px',
            fontSize: '18px',
            borderRadius: '25px',
            color: 'white',
            fontWeight: '600',
            marginTop: '2rem',
            cursor: 'pointer'
          }}>
            Start Your Journey
          </button>
        </div>
      </div>
    </div>
  );
}